import { computed, ref } from "@vue/composition-api";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { PrivateLiveChat, PrivateLiveChatStatus } from "@scrile/api-provider/dist/api/PrivateLivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import config from "@/config";
import useLiveChat from "./useLiveChat";

export const usePrivateLiveChat = ({ userId }: { userId: string }) => {
  const { authUser, isAdmin } = useAuthUser();

  const privateLiveChats = ref<PrivateLiveChat[]>([]);
  let checkingPrivateLive = Promise.resolve();

  const privateLiveChat = computed(() =>
    isAdmin.value
      ? privateLiveChats.value[0]
      : privateLiveChats.value.find(i => [i.providerId, i.consumerId].includes(authUser.value?.id ?? ""))
  );
  const consumer = computed(() => privateLiveChat.value?.consumer);

  const subject = computed<Subject<LivechatSubjectType>>(() => ({
    subjectId: privateLiveChat.value?.id ?? "",
    subjectType: LivechatSubjectType.PRIVATE_LIVECHAT,
  }));

  const { loading, joinData, clientServerTimeDiff, errorData, joinChat, endChat } = useLiveChat({
    subject,
  });

  const isPrivateLive = computed(() => !!joinData.value);

  const searchPrivateLiveChats = async () => {
    if (!authUser.value) return;
    privateLiveChats.value = await providers.PrivateLiveChatsProvider.search({
      data: {
        providerId: userId,
        consumerId: authUser.value.id === userId || isAdmin.value ? undefined : authUser.value.id,
        status: PrivateLiveChatStatus.ACTIVE,
      },
    });
  };

  const joinPrivateLive = async (connectionDelay = 0) => {
    await searchPrivateLiveChats();
    if (privateLiveChat.value) await joinChat(connectionDelay);
  };

  const endPrivateLive = async () => {
    await endChat();
    privateLiveChats.value = [];
  };

  if (config.featurePrivateLives) {
    checkingPrivateLive = joinPrivateLive();
  }

  return {
    loading,
    joinData,
    clientServerTimeDiff,
    errorData,
    isPrivateLive,
    checkingPrivateLive,
    consumer,
    joinPrivateLive,
    endPrivateLive,
  };
};
